/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState, useRef } from "react";
import { useFormState, Controller, useWatch } from "react-hook-form";
import PropTypes from "prop-types";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InputMask from "../../InputMask";
import { getSocialSecurityNumber } from "../../../utils/helpers";
import { generateFormFieldRules } from "../services/form.service";

function FormEncryptedField(props) {
  const { fieldProps, control, formProps, disabled, register, setValue } =
    props;
  const { errors } = useFormState({ control, name: fieldProps.key });
  const isMasked = useWatch({
    name: `${fieldProps.key}isMasked__DUMMY`,
    defaultValue: true,
    control,
  });
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  const toggleFieldMasked = async (toggleValue) => {
    const value = typeof toggleValue === "boolean" ? toggleValue : !isMasked;
    setValue(`${fieldProps.key}isMasked__DUMMY`, value);
  };

  useEffect(() => {
    register(`${fieldProps.key}isMasked__DUMMY`);
    setValue(`${fieldProps.key}isMasked__DUMMY`, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rules = generateFormFieldRules(fieldProps);
  const showActualValue = isFocused || !isMasked;

  const handleFocus = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsFocused(true);
    toggleFieldMasked(false);
    // Store timeout reference for cleanup
    timeoutRef.current = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
      timeoutRef.current = null;
    }, 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
    toggleFieldMasked(true);
  };

  const inputRefHandler = (element, ref) => {
    if (ref) {
      if (typeof ref === "function") {
        ref(element);
        // eslint-disable-next-line no-param-reassign
      } else ref.current = element;
    }
    inputRef.current = element;
  };

  const onChangeHandler = (e, onChange) => {
    if (showActualValue) {
      // When showing actual value, allow normal input
      const inputValue = e.target.value;
      const cleanValue = inputValue.replace(/[^\d]/g, "");
      onChange(cleanValue);
    }
    // When showing X characters, don't allow editing
    else e.preventDefault();
  };

  const slotProps = {
    input: {
      inputComponent: showActualValue ? InputMask : undefined,
      inputProps: { mask: "000-00-0000" },
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle encrypted visibility"
            onClick={(event) => {
              event.stopPropagation();
              toggleFieldMasked();
            }}
            edge="end"
          >
            {showActualValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <FormControl fullWidth margin="dense" sx={{ minHeight: "4.6875rem" }}>
      <Controller
        name={fieldProps.key}
        control={control}
        defaultValue={formProps[fieldProps.key]}
        rules={rules}
        render={({ field: { onChange, onBlur, ref, value, ...field } }) => (
          <TextField
            {...field}
            inputRef={(element) => inputRefHandler(element, ref)}
            name={fieldProps.key}
            label={fieldProps.label}
            id={fieldProps.key}
            aria-describedby={
              errors[fieldProps.key] ? `${fieldProps.key}-error` : undefined
            }
            disabled={disabled}
            required={fieldProps.validate?.required}
            error={!!errors[fieldProps.key]}
            variant="standard"
            value={getSocialSecurityNumber({ isMasked, value, fieldProps })}
            onFocus={handleFocus}
            onBlur={(e) => {
              handleBlur();
              onBlur(e);
            }}
            onClick={handleFocus}
            onChange={(e) => onChangeHandler(e, onChange)}
            slotProps={slotProps}
            autoComplete="off"
          />
        )}
      />
      {errors[fieldProps.key] && (
        <FormHelperText error id={`${fieldProps.key}-error`} sx={{ margin: 0 }}>
          This is required.
        </FormHelperText>
      )}
      <div />
    </FormControl>
  );
}
FormEncryptedField.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    validate: PropTypes.shape({
      required: PropTypes.bool,
    }),
  }).isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  formProps: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  disabled: PropTypes.bool.isRequired,
};
export default FormEncryptedField;
